import { Autocomplete, Avatar, Box, FormControl, InputAdornment, TextField, useTheme } from '@mui/material'
import Popper from '@mui/material/Popper'
import React, { useEffect, useState } from 'react'
import { renderTeamOption } from '../Team/renderOptions'

interface SingleUserSelectProps {
  name: string
  value: any // Expect a user object or null
  options: any[]
  displayLabel: string
  onChange: (user: any | null) => void
  inputRef?: React.RefObject<HTMLInputElement>
  disabled?: boolean
  sx?: object
  setNotifyIds?: React.Dispatch<React.SetStateAction<string[]>>
  border?: string
}
const CustomPopper = (props: any) => {
  return (
    <Popper
      {...props}
      placement="bottom-start"
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 4],
          },
        },
      ]}
      sx={{
        zIndex: 1300,
        minWidth: 250,
        maxWidth: 420,
        width: 'auto',
      }}
    />
  )
}

const SingleUserSelect: React.FC<SingleUserSelectProps> = ({
  name,
  value,
  options,
  displayLabel,
  onChange,
  inputRef,
  disabled,
  sx,
  setNotifyIds,
  border,
}) => {
  const theme = useTheme()
  const [selectedUser, setSelectedUser] = useState<any>(value || null)

  useEffect(() => {
    setSelectedUser(value || null)
  }, [value])

  const handleChange = (_event: any, newValue: any | null) => {
    if (!newValue) {
      setSelectedUser(null)
      onChange(null) // just send null
      return
    }

    if (newValue?.id !== value?.id && setNotifyIds) {
      setNotifyIds(prev => [...prev, newValue.id])
    }

    setSelectedUser(newValue)
    onChange(newValue) // just send the user object
  }

  const safeOptions = options || []

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <FormControl fullWidth>
        <Autocomplete
          options={safeOptions}
          PopperComponent={CustomPopper}
          value={safeOptions.find(opt => opt.user_uuid === selectedUser?.user_uuid) || null}
          onChange={handleChange}
          getOptionLabel={option => option?.name || ''}
          isOptionEqualToValue={(option, value) => {
            if (!option || !value) return false
            return option.user_uuid === value.user_uuid
          }}
          filterOptions={(opts, { inputValue }) => {
            const searchTerm = inputValue.toLowerCase()
            return opts.filter(
              option =>
                option.name?.toLowerCase().includes(searchTerm) ||
                (option.role && option.role.toLowerCase().includes(searchTerm))
            )
          }}
          renderOption={(props, option, state) => renderTeamOption(props, option, state, theme)}
          renderInput={params => (
            <TextField
              {...params}
              label={displayLabel}
              variant="outlined"
              inputRef={inputRef}
              placeholder="Search by name or role"
              InputProps={{
                ...params.InputProps,
                startAdornment: selectedUser?.image_512 ? (
                  <InputAdornment position="start">
                    <Avatar
                      src={selectedUser.image_512}
                      alt={selectedUser.name}
                      sx={{ width: 30, height: 30, mr: 1 }}
                    />
                  </InputAdornment>
                ) : null,
                endAdornment: null,
              }}
              sx={{
                maxHeight: '45px',
                '& .MuiAutocomplete-inputRoot': {
                  maxHeight: '45px',
                  height: '45px',
                },
                '& .MuiAutocomplete-input': {
                  minWidth: 140, // Adjust this to allow more name space
                  flex: '1 1 auto',
                },
                '& .MuiInputBase-inputAdornedStart': {
                  paddingLeft: 0,
                },
                '& .MuiOutlinedInput-notchedOutline': { border: border ?? '' },
              }}
            />
          )}
          disabled={disabled}
          fullWidth
          sx={{
            '& .MuiInputBase-root': {
              margin: 0,
              p: 0,
              paddingLeft: 1,
            },
          }}
        />
      </FormControl>
    </Box>
  )
}

export default SingleUserSelect
